import { useMsal } from '@azure/msal-react';
import logoutIcon from '@src/main/assets/icons/logout.svg';
import logoFull from '@src/main/assets/images/iberia-logo.svg';
import { PERMISSIONS } from '@src/main/shared/constants/permissions';
import { useAuth } from '@src/main/shared/context/AuthContext';
import styles from '@src/main/shared/ui/components/navbar/Navbar.module.scss';
import { Avatar } from 'primereact/avatar';
import { Menubar } from 'primereact/menubar';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

const Navbar: FC = () => {
  const [items, setItems] = useState<MenuItem[]>([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMobileItems, setExpandedMobileItems] = useState<Record<string, boolean>>({});
  const { user, hasPermission } = useAuth();

  interface MenuItem {
    label: string;
    icon?: string;
    url?: string;
    command?: () => void;
    items?: MenuItem[];
  }

  const { t } = useTranslation();
  const { instance } = useMsal();
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    instance.logoutRedirect({
      postLogoutRedirectUri: '/login',
    });
  };

  const handleMobileGroupClick = (label: string) => {
    setExpandedMobileItems((currentItems) => ({
      ...currentItems,
      [label]: !currentItems[label],
    }));
  };

  const renderMobileMenuItem = (item: MenuItem) => {
    const isExpanded = Boolean(expandedMobileItems[item.label]);
    const hasChildren = Boolean(item.items?.length);
    const mobileItemClassName = `${styles['mobile-menu-item']} ${
      isExpanded ? styles['mobile-menu-item-active'] : ''
    }`;

    if (hasChildren) {
      return (
        <li key={item.label}>
          <button
            type="button"
            className={mobileItemClassName}
            onClick={() => handleMobileGroupClick(item.label)}
          >
            <span>{item.label}</span>
            <i className={`pi ${isExpanded ? 'pi-chevron-up' : 'pi-chevron-down'}`} />
          </button>
          {isExpanded && (
            <ul className={styles['mobile-submenu']}>
              {item.items?.map((childItem) => renderMobileMenuItem(childItem))}
            </ul>
          )}
        </li>
      );
    }

    return (
      <li key={item.label}>
        <button type="button" className={styles['mobile-menu-item']} onClick={item.command}>
          <span>{item.label}</span>
        </button>
      </li>
    );
  };

  useEffect(() => {
    const reportsItems: MenuItem[] = [];

    if (hasPermission(PERMISSIONS.REPORTS.view)) {
      reportsItems.push({
        label: t('reports.menu.punctuality'),
        command: () => navigate('/reports'),
      });
    }

    if (hasPermission(PERMISSIONS.EVALUATIONS.view)) {
      reportsItems.push({
        label: t('reports.menu.evaluations'),
        command: () => navigate('/reports/evaluations'),
      });
    }
    if (hasPermission(PERMISSIONS.EVALUATIONS.view)) {
      reportsItems.push({
        label: t('reports.menu.positionassignment'),
        command: () => navigate('/reports/positionassignment'),
      });
    }
    if (hasPermission(PERMISSIONS.REPORTS.view)) {
      reportsItems.push({
        label: t('reports.menu.incapacitated'),
        command: () => navigate('/reports/incapacitated'),
      });
    }

    const menuItems: MenuItem[] = [
      {
        label: t('parameters.title'),
        items: [
          {
            label: t('parameters.fleetsAndPositions.title'),
            command: () => navigate('/parameters/positions_fleets'),
          },
          {
            label: t('parameters.fleetAssignments.title'),
            command: () => navigate('/parameters/fleet_assignment'),
          },
          {
            label: t('parameters.parametersManagement.title'),
            command: () => navigate('/parameters/manage_parameters'),
          },
          {
            label: t('parameters.releaseNotes.title'),
            command: () => navigate('/parameters/release_notes'),
          },
          {
            label: t('parameters.releaseNotesByod.title'),
            command: () => navigate('/parameters/release_notes_byod'),
          },
          {
            label: t('parameters.issues.title'),
            command: () => navigate('/parameters/issues'),
          },
        ],
      },
      {
        label: t('messages.title'),
        items: [
          {
            label: t('messages.mailbox.title'),
            command: () => navigate('/messages/mailbox'),
          },
          {
            label: t('messages.subjects.title'),
            command: () => navigate('/messages/subjects'),
          },
          {
            label: t('messages.addresses.title'),
            command: () => navigate('/messages/addresses'),
          },
        ],
      },
      {
        label: t('administration.menu.title'),
        items: [
          {
            label: t('usersManagement.title'),
            command: () => navigate('/users-management'),
          },
          {
            label: t('rolesManagement.title'),
            command: () => navigate('/roles-management'),
          },
        ],
      },
    ];

    if (reportsItems.length > 0) {
      menuItems.push({
        label: t('reports.title'),
        items: reportsItems,
      });
    }
    menuItems.push({
      label: t('incidents.menu.title'),
      items: [
        {
          label: t('appStatus.title'),
          command: () => navigate('/app-status'),
        },
        {
          label: t('otherIncidentManagement.title'),
          command: () => navigate('/other-incidents'),
        },
      ],
    });
    setItems(menuItems);
  }, [hasPermission, navigate, t]);

  const mobileMenuToggleClassName = `${styles['mobile-menu-toggle']} ${
    isMobileMenuOpen ? styles['mobile-menu-toggle-active'] : ''
  }`;

  return (
    <div className={styles['navbar-container']}>
      <Menubar
        model={items}
        className={styles['custom-menubar']}
        pt={{
          menuitem: { className: styles['custom-menu-item'] },
          label: { className: styles['custom-menu-item-label'] },
          root: { className: styles['custom-menubar-root'] },
          menu: { className: styles['custom-menubar-menu'] },
          submenu: { className: styles['custom-submenu'] },
        }}
        start={
          <div className={styles['navbar-start']}>
            <div className={`${styles['navbar-logo']} ${styles['navbar-logo-small']} `}>
              <Link to="/">
                <img className={styles['text-logo']} src={logoFull} alt="Iberia title and logo" />
                <img className={styles['only-logo']} src={logoFull} alt="Iberia logo" />
              </Link>
            </div>
            <button
              type="button"
              className={mobileMenuToggleClassName}
              aria-expanded={isMobileMenuOpen}
              aria-label="Abrir menú"
              onClick={() => setIsMobileMenuOpen((isOpen) => !isOpen)}
            >
              <i className="pi pi-bars" />
            </button>
          </div>
        }
        end={
          <div className={styles['navbar-user']}>
            <span className={styles['navbar-username']}>
              {user?.fullName} ({user?.employeeId})
            </span>
            <Avatar
              className={styles['navbar-avatar']}
              image={logoutIcon}
              shape="circle"
              size="normal"
              onClick={handleLogout}
            />
          </div>
        }
        menuIcon="pi pi-bars"
      />

      {isMobileMenuOpen && (
        <ul className={styles['mobile-menu']}>{items.map(renderMobileMenuItem)}</ul>
      )}
    </div>
  );
};

export default Navbar;
